/**
 * Пул водителя: кого можно выбрать, как назвать отказ сервера, в каком
 * порядке рисовать маршрут.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ВЫБОРКА — ТА ЖЕ, ЧТО У «ЗАБИРАЮ», А ПУЛ — СЕРВЕРНЫЙ
 * ─────────────────────────────────────────────────────────────────────────
 * Отмеченные байки живут в `lib/pickup-selection.ts`, как и раньше: туда
 * же пишут список и карточка. Пулом выборка становится только по
 * «Забрать» (`POST /v1/scout/pool`), и с этого момента правда о пуле —
 * ответ сервера, а не локальная заметка. Поэтому здесь нет хранилища,
 * только чистые функции над выборкой и ответом сервера.
 *
 * Лист: никаких `api/*` и очереди — экраны передают сюда уже прочитанное.
 */
import { hasPosition, type LatLng } from './geo'
import { CAR_CAPACITY, isChargedAtBase } from './route-pick'
import { heldByCollectorLabel, vehicleStatusLabel } from './vehicle-status'

/** Больше активных (едем + в машине) сервер не берёт — `POOL_LIMIT`. */
export const POOL_LIMIT = 30

/**
 * Статусы, из которых байк можно взять в пул. Зеркало CreatePool во fleet:
 * ещё `at_base`, если он заряжен для хаба (`isChargedAtBase`).
 */
const POOLABLE = new Set(['available', 'faulty'])

export type PoolEligibility =
  | { kind: 'selectable' }
  | { kind: 'mine' }
  | { kind: 'other' }
  | { kind: 'blocked'; reason: string }

type EligibleBike = {
  id: string
  status: string
  poolDriverId: string
  /** C1: `in_ride` — байк под райдером, даже если статус ещё `available`. */
  problem?: string
  /** Кто держит: "" | driver | collector. */
  poolKind?: string
  /** Для `at_base`: заряд и его свежесть — заряженный едет в хаб. */
  batteryPct?: number
  runtimeStale?: boolean | undefined
}

/**
 * Можно ли отметить байк для пула.
 *
 * `poolIds` — байки из `GET /v1/scout/pool`: парк перечитывается раз в
 * 20 с и может ещё не знать водителя, а пул уже вернул байк как мой.
 * Без `userId` (сессия не прочитана) любой водитель в байке — чужой:
 * лучше серый свой байк на секунду, чем выбранный чужой.
 */
export function poolEligibility(
  bike: EligibleBike,
  ctx: { userId: string; poolIds: ReadonlySet<string>; baseChargedMinPct?: number },
): PoolEligibility {
  // Байк сборщика — не работа водителя, даже если держатель он сам (staff,
  // проверявший режим сборщика): `poolDriverId` у сборщика тот же.
  if (bike.poolKind === 'collector') {
    return { kind: 'blocked', reason: heldByCollectorLabel(bike.status, 'collector') ?? 'У сборщика' }
  }
  if (ctx.poolIds.has(bike.id)) return { kind: 'mine' }
  if (bike.poolDriverId !== '') {
    return bike.poolDriverId === ctx.userId ? { kind: 'mine' } : { kind: 'other' }
  }
  if (bike.problem === 'in_ride') return { kind: 'blocked', reason: vehicleStatusLabel.in_ride! }
  if (POOLABLE.has(bike.status)) return { kind: 'selectable' }
  if (
    bike.batteryPct !== undefined &&
    isChargedAtBase({ status: bike.status, batteryPct: bike.batteryPct, runtimeStale: bike.runtimeStale }, ctx.baseChargedMinPct)
  ) {
    return { kind: 'selectable' }
  }
  if (bike.status === '') return { kind: 'blocked', reason: 'Недоступен' }
  return { kind: 'blocked', reason: vehicleStatusLabel[bike.status] ?? bike.status }
}

/**
 * Что уходит на сервер по «Забрать» и что считает «Выбрано N».
 *
 * Выборку пишут ещё список, карточка и «все на экране», и серые байки
 * туда тоже попадают. Байк, которого нет в парке, — незнание, не кандидат.
 */
export function poolCandidates(
  selected: ReadonlySet<string>,
  eligibility: ReadonlyMap<string, PoolEligibility>,
): string[] {
  return [...selected].filter((id) => eligibility.get(id)?.kind === 'selectable')
}

/** Тело `POST /v1/scout/pool`. Без геопозиции координат нет вовсе — (0,0) их не заменяет. */
export function poolCreateBody(
  vehicleIds: readonly string[],
  me: LatLng | null,
  destinations?: Readonly<Record<string, 'unload' | 'base'>>,
): {
  vehicleIds: string[]
  lat?: number
  lng?: number
  destinations?: Record<string, 'unload' | 'base'>
} {
  const body: ReturnType<typeof poolCreateBody> = { vehicleIds: [...vehicleIds] }
  if (me) {
    body.lat = me.lat
    body.lng = me.lng
  }
  if (destinations) body.destinations = { ...destinations }
  return body
}

/** «HUB <имя>»; в bo хабы обычно уже названы «HUB …». */
export function hubLabel(name: string): string {
  const n = name.trim()
  return /^hub/i.test(n) ? n : `HUB ${n}`
}

/**
 * «→ HUB Цирк» / «→ хаб» (хаб ещё не назначен) / «→ база» у пункта пула;
 * незнакомое или пустое назначение — как база.
 */
export function destinationText(destination: string | undefined, hubName = ''): string {
  if (destination !== 'unload') return '→ база'
  return hubName ? `→ ${hubLabel(hubName)}` : '→ хаб'
}

export function eligibilityText(e: PoolEligibility): string {
  switch (e.kind) {
    case 'selectable':
      return ''
    case 'mine':
      return 'В вашем пуле'
    case 'other':
      return 'В пуле у другого водителя'
    case 'blocked':
      return e.reason
  }
}

/**
 * Причина отказа по байку из ответа «Забрать».
 *
 * По `reason`, а не по тексту — правило CLAUDE.md. Незнакомый reason
 * показывается как есть: новая причина на сервере не должна превращаться
 * в молчаливо пропавший байк.
 */
const REJECT_TEXT: Record<string, string> = {
  VEHICLE_NOT_POOLABLE: 'статус не позволяет взять в пул',
  VEHICLE_IN_USE: 'в аренде или забронирован',
  VEHICLE_TASK_TAKEN: 'задачу на него уже взял другой скаут',
  VEHICLE_IN_POOL: 'уже в пуле у другого водителя',
  POOL_LIMIT: `машина полная — больше ${CAR_CAPACITY} в машине одновременно (или ${POOL_LIMIT} в пуле) не берём`,
  VEHICLE_NOT_FOUND: 'байк не найден',
}

export function poolRejectText(reason: string): string {
  if (reason === '') return 'сервер не взял'
  return REJECT_TEXT[reason] ?? reason
}

type RouteItem = { vehicleId: string; position: number; state: string; lat: number; lng: number }

/**
 * Точки маршрута — только «едем за ним», в серверном порядке.
 *
 * Порядок не пересчитывается на клиенте: тот же номер стоит в
 * Telegram-сообщении, и жадный «ближайший следующий» отсюда дал бы другую
 * нумерацию, чем в чате. Забранные (в машине) и закрытые — не адреса.
 *
 * `fresh` — позиции из парка: он перечитывается чаще, чем пул, а пункт
 * пула несёт снимок на момент ответа. Пустая (0,0) позиция парка — это
 * «неизвестно», и снимок пула она не затирает.
 */
export function poolRoute<T extends RouteItem>(
  items: readonly T[],
  fresh?: ReadonlyMap<string, LatLng>,
): T[] {
  return items
    .filter((i) => i.state === 'pooled')
    .map((i) => {
      const live = fresh?.get(i.vehicleId)
      return live && hasPosition(live) ? { ...i, lat: live.lat, lng: live.lng } : i
    })
    .filter(hasPosition)
    .sort((a, b) => a.position - b.position)
}

/**
 * Координаты ломаной для слоя карты: [долгота, широта], от водителя.
 *
 * Без геопозиции линия начинается с первой точки — маршрут между байками
 * всё равно полезен. Меньше двух точек — не линия.
 */
export function routeLine(me: LatLng | null, route: readonly LatLng[]): [number, number][] {
  const points = me ? [me, ...route] : [...route]
  if (points.length < 2) return []
  return points.map((p) => [p.lng, p.lat])
}

/**
 * Что видно на карте в режиме «Пул».
 *
 * Фильтры работают как обычно — по ним водитель и ищет, что забрать. Но
 * свой пул и уже отмеченные держатся на карте вопреки им: байк, который
 * водитель сам выбрал, не должен пропадать из-за чипа.
 */
export function poolVisible<T extends { id: string; status: string }>(
  vehicles: readonly T[],
  opts: {
    matches: (v: T) => boolean
    hidden: ReadonlySet<string>
    exempt: ReadonlySet<string>
  },
): T[] {
  return vehicles.filter(
    (v) => opts.exempt.has(v.id) || (opts.matches(v) && !opts.hidden.has(v.status)),
  )
}

export const POOL_MODE_KEY = 'scout.map.pool'

/**
 * Режим «Пул» между запусками — читательское удобство, как скрытые статусы
 * (`lib/map-filter.ts`): здесь флаг, не данные API.
 */
export function readPoolMode(): boolean {
  try {
    return localStorage.getItem(POOL_MODE_KEY) === '1'
  } catch {
    return false
  }
}

export function writePoolMode(on: boolean): void {
  try {
    if (on) localStorage.setItem(POOL_MODE_KEY, '1')
    else localStorage.removeItem(POOL_MODE_KEY)
  } catch {
    /* приватный режим/квота — просто не запомним */
  }
}

/* ─── Карточка, список и смена ─────────────────────────────────────────── */

/**
 * Состояние пункта с поправкой на неотправленные отметки. Сверх серверных
 * состояний — `closing`: «Поставил»/«Убрать» лежит в очереди.
 *
 * Сервер узнаёт о «Забрал»/«Поставил» только после досылки, а пул
 * перечитывается раз в 20 с. Без поправки рефетч до досылки вернул бы
 * кнопки, и второй тап ушёл бы в 409 POOL_ITEM_STATE. `picking`/`closing` —
 * цели записей `pool_pick` и `pool_deliver`+`pool_drop` в очереди.
 */
export function effectivePoolState(
  item: { vehicleId: string; state: string },
  queued: { picking: ReadonlySet<string>; closing: ReadonlySet<string> },
): string {
  if (queued.closing.has(item.vehicleId)) return 'closing'
  if (queued.picking.has(item.vehicleId) && item.state === 'pooled') return 'picked'
  return item.state
}

/**
 * Цели неотправленных отметок пула — вход для `effectivePoolState`.
 * Записи очереди принимаются по форме, а не по типу: модуль — лист.
 */
export function poolQueue(entries: readonly { kind: string; targetId: string }[]): {
  picking: Set<string>
  closing: Set<string>
} {
  const picking = new Set<string>()
  const closing = new Set<string>()
  for (const e of entries) {
    if (e.kind === 'pool_pick') picking.add(e.targetId)
    else if (e.kind === 'pool_deliver' || e.kind === 'pool_drop') closing.add(e.targetId)
  }
  return { picking, closing }
}

const ACTIVE = new Set(['pooled', 'picked'])

/**
 * Что показать на карточке байка: панель своего пула, пометку чужого или
 * ничего. Партнёр пул не видит никогда — сервер ему его и не откроет.
 */
export function poolPanelFor<T extends { vehicleId: string; state: string }>(args: {
  isPartner: boolean
  userId: string
  vehicle: { id: string; poolDriverId: string; poolKind?: string }
  items: readonly T[]
}): { kind: 'mine'; item: T } | { kind: 'other' } | { kind: 'collector' } | null {
  if (args.isPartner) return null
  if (args.vehicle.poolKind === 'collector') return { kind: 'collector' }
  const item = args.items.find((i) => i.vehicleId === args.vehicle.id && ACTIVE.has(i.state))
  if (item) return { kind: 'mine', item }
  const driver = args.vehicle.poolDriverId
  if (driver !== '' && driver !== args.userId) return { kind: 'other' }
  return null
}

export type DeliverResult = 'line' | 'charge' | 'broken'

export const deliverResultLabel: Record<DeliverResult, string> = {
  line: 'На линию',
  charge: 'На зарядку',
  broken: 'Сломан',
}

/**
 * Тело «Поставил».
 *
 * «На линию» без геопозиции сервер отклонит (400 POSITION_REQUIRED) — но
 * через очередь, когда водитель уже уехал. Поэтому отказ здесь, у байка.
 */
export function deliverPayload(
  result: DeliverResult,
  me: LatLng | null,
): { ok: true; payload: Record<string, unknown> } | { ok: false; error: string } {
  if (result === 'line' && !me) {
    return { ok: false, error: 'Нужна ваша геопозиция — без неё на линию не поставить' }
  }
  return { ok: true, payload: me ? { result, lat: me.lat, lng: me.lng } : { result } }
}

export type DropReason = 'not_found' | 'other'

export const dropReasonLabel: Record<DropReason, string> = {
  not_found: 'Не нашёл',
  other: 'Другое',
}

/** Предел комментария в BFF — 300 символов (рун, не байт). */
export const DROP_COMMENT_MAX = 300

/** Тело «Убрать из пула». Пустой комментарий не отправляется вовсе. */
export function dropPayload(
  reason: DropReason,
  comment: string,
  me: LatLng | null,
): Record<string, unknown> {
  const text = Array.from(comment.trim()).slice(0, DROP_COMMENT_MAX).join('')
  return {
    reason,
    ...(text ? { comment: text } : {}),
    ...(me ? { lat: me.lat, lng: me.lng } : {}),
  }
}

/** Список пула: «едем за ним» по номеру маршрута, затем «в машине». */
export function poolListOrder<T extends { state: string; position: number }>(items: readonly T[]): T[] {
  const rank = (i: T) => (i.state === 'pooled' ? 0 : 1)
  return [...items].sort((a, b) => rank(a) - rank(b) || a.position - b.position)
}

/** Остановка маршрута сервера — форма `PoolStop` из `api/pool.ts` (модуль — лист). */
type RouteStop = { kind: string; vehicleIds: readonly string[]; name: string; lat: number; lng: number; loadBefore: number; loadAfter: number }

/**
 * Точки линии маршрута по остановкам сервера: подбор — позиция байка из
 * парка (свежее снимка), иначе из остановки; хаб и база — свои. (0,0) —
 * «неизвестно», не точка.
 */
export function poolStopPoints(stops: readonly RouteStop[], fresh?: ReadonlyMap<string, LatLng>): LatLng[] {
  const out: LatLng[] = []
  for (const s of stops) {
    const live = s.kind === 'pickup' ? fresh?.get(s.vehicleIds[0] ?? '') : undefined
    const p = live && hasPosition(live) ? live : { lat: s.lat, lng: s.lng }
    if (hasPosition(p)) out.push({ lat: p.lat, lng: p.lng })
  }
  return out
}

export type PoolSheetRow<T> =
  | { kind: 'item'; item: T }
  | { kind: 'unload'; place: 'hub' | 'base'; title: string; load: string }

/**
 * Строки листа «Мой пул». С маршрутом сервера — в порядке езды: сначала то,
 * что уже в машине, потом подборы, и между ними выгрузки («HUB Цирк —
 * выгрузить 2: №…», «в машине: 2 → 0»). Без маршрута (старый сервер, хабы не
 * прочитались) — прежний `poolListOrder`. Пункт, которого в маршруте нет
 * (сервер о нём ещё не знает), — в конце, чтобы не пропал.
 */
export function poolSheetRows<T extends { vehicleId: string; state: string; position: number; number: string }>(
  items: readonly T[],
  stops: readonly RouteStop[],
): PoolSheetRow<T>[] {
  if (stops.length === 0) return poolListOrder(items).map((item) => ({ kind: 'item', item }))
  const byId = new Map(items.map((i) => [i.vehicleId, i]))
  const shown = new Set<string>()
  const rows: PoolSheetRow<T>[] = []
  const add = (i: T | undefined) => {
    if (!i || shown.has(i.vehicleId)) return
    shown.add(i.vehicleId)
    rows.push({ kind: 'item', item: i })
  }
  for (const i of items) if (i.state === 'picked') add(i)
  for (const s of stops) {
    if (s.kind === 'pickup') {
      add(byId.get(s.vehicleIds[0] ?? ''))
      continue
    }
    const nums = s.vehicleIds.map((id) => `№${byId.get(id)?.number || id.slice(0, 8)}`).join(', ')
    const where = s.kind === 'hub' ? hubLabel(s.name) : 'База'
    rows.push({
      kind: 'unload',
      place: s.kind === 'hub' ? 'hub' : 'base',
      title: `${where} — выгрузить ${s.vehicleIds.length}: ${nums}`,
      load: `в машине: ${s.loadBefore} → ${s.loadAfter}`,
    })
  }
  for (const i of poolListOrder(items)) add(i)
  return rows
}

/**
 * «В пуле 1 ч 20 мин» — сколько байк в пуле, от RFC3339 момента добавления.
 *
 * Не `timeAgo`: тот отвечает «когда» («5 мин назад»), а здесь вопрос
 * «как долго». Будущее (часы телефона и сервера расходятся) — «меньше
 * минуты». Пустое или непарсящееся — `null`: строки не будет вовсе.
 */
export function poolDurationText(since: string, now: Date = new Date()): string | null {
  if (since === '') return null
  const then = new Date(since).getTime()
  if (Number.isNaN(then)) return null
  const min = Math.floor((now.getTime() - then) / 60_000)
  if (min < 1) return 'в пуле меньше минуты'
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h === 0) return `в пуле ${m} мин`
  return m === 0 ? `в пуле ${h} ч` : `в пуле ${h} ч ${m} мин`
}

/** Заряд из пункта пула: -1 — сервер не знает (снимка нет). */
export function poolBatteryText(pct: number): string {
  return pct < 0 ? 'нет связи' : `${pct} %`
}

type ReportItem = { number: string; qrToken: string; vehicleId: string; batteryPct: number }

/**
 * Отчёт смены разделами — в порядке ТЗ. Пустой раздел не показывается:
 * «Поломаны (0)» читается как работа, которой не было.
 */
export function shiftReportSections(r: {
  charged: readonly ReportItem[]
  broken: readonly ReportItem[]
  onLine: readonly ReportItem[]
  forgotten: readonly ReportItem[]
  dropped: readonly ReportItem[]
  inCar: readonly ReportItem[]
}): { title: string; lines: string[] }[] {
  const num = (i: ReportItem) => `№${i.number || i.qrToken || i.vehicleId.slice(0, 8)}`
  const sections: [string, readonly ReportItem[], (i: ReportItem) => string][] = [
    ['На зарядку', r.charged, num],
    ['Поломаны', r.broken, num],
    // ТЗ: «на линию — с % заряда каждого».
    ['На линию', r.onLine, (i) => `${num(i)} · ${poolBatteryText(i.batteryPct)}`],
    ['Забытые', r.forgotten, num],
    ['Убраны', r.dropped, num],
    ['В машине', r.inCar, num],
  ]
  return sections
    .filter(([, items]) => items.length > 0)
    .map(([title, items, line]) => ({ title, lines: items.map(line) }))
}
